// @flow
import { Container, CssBaseline, List, ListItem, ListItemIcon, ListItemText, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Build as DraftIcon,
  HourglassEmpty as ValidatingIcon,
  WhatsApp as SupportIcon,
  Work as ProcessingIcon
} from '@material-ui/icons';
import { Skeleton } from '@material-ui/lab';
import { useSnackbar } from 'notistack';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import routes from '../routes';
import ProcessAgreementData from '../component/ProcessAgreementData';
import { AuthContext, setAuthToken } from '../context/auth';
import SignProcess, { ProcessResponse } from '../model/SignProcess';
import { toSentence } from '../util/string';
import { whatsappURL } from '../util/vendor';
import ApiResponse from '../model/ApiResponse';
import type { AgreementProps } from '../model/props';

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(2),
  },
  draft: {
    color: theme.palette.secondary[ '900' ],
  },
  validating: {
    color: theme.palette.grey[ '700' ],
  },
  processing: {
    color: theme.palette.grey[ '900' ],
  },
  grey: {
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error[ '600' ],
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(14),
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  bottomButton: {
    marginTop: theme.spacing(2),
  },
}));

type Props = {
  isRetailClient: boolean,
  match?: any,
};

const Contract = (props: Props) => {
  const { user, doGet } = useContext(AuthContext);
  const { t } = useTranslation();
  const { match, isRetailClient } = props;
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [supportPhone, setSupportPhone] = useState<?string>();
  const [agentPhone, setAgentPhone] = useState<?string>();
  const [process, setProcess] = useState<?SignProcess>();
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState<?string>();

  const loadData = () => {
    if (!process
      && match && match.params
      && match.params.code && match.params.token) {
      let path = 'retail';
      setAuthToken(null);
      if (!isRetailClient) {
        setAuthToken(match.params.token);
        path = 'token';
      }
      doGet(`/customer/${path}-sign-process/${match.params.code}/${match.params.token}`)
        .then((response: ApiResponse) => {
          if (response.ok) {
            setProcess(SignProcess.parse(response.data));
          } else {
            setError(response.message || 'Error');
          }
        });
      if (!supportPhone) {
        doGet('/customer/support-phone')
          .then((response: ApiResponse) => setSupportPhone(response.data));
      }
      if (!agentPhone) {
        doGet('/customer/agent-phone')
          .then((response: ApiResponse) => setAgentPhone(response.data));
      }
    }
  };

  useEffect(loadData, []);

  const saveAgreementData = (data: AgreementProps): Promise<boolean> => {
    if (!!process && !process.hasContractAgreementApproved) {
      let theData = data;
      if (match && match.params && match.params.token) {
        theData.token = match.params.token;
      }
      setProcessing(true);
      return new Promise(resolve => {
        process.saveAgreement(theData)
          .then((response: ProcessResponse) => {
            if (response.ok) {
              setProcess(response.process);
              enqueueSnackbar(t('Contract agreement saved'), {
                variant: 'success',
              });
            } else if (!!response.message) {
              enqueueSnackbar(t(response.message), {
                variant: 'error',
              });
            }
            setProcessing(false);
            resolve(response.ok);
          });
      });
    }
    return new Promise<boolean>(resolve => resolve(false));
  };

  if (!match || !match.params || !match.params.code) {
    return <Redirect to={routes.home}/>;
  }

  let contractAgreementUrl = '';
  let templateAgreementUrl = '';
  if (!!process) {
    contractAgreementUrl = process.getContractAgreementUrl(match.params.token);
    templateAgreementUrl = process.getTemplateAgreementUrl(match.params.token);
  }

  return (
    <Container component="main" maxWidth="xl">
      <CssBaseline/>
      <div className={classes.paper}>
        {(!process && !error) && [1, 2, 3, 4, 5].map((index) => (
          <Skeleton key={index}/>
        ))}
        {!!error && (
          <>
            <Typography variant="subtitle2" className={classes.error}>
              {t(error)}
            </Typography>
          </>
        )}
        {!!process && (
          <>
            <Typography variant="h5">
              {process.isDraft()
              && <DraftIcon className={classes.draft}/>}
              {(process.hasVideoLoaded() && ! process.isProcessing() && !process.isCompleted())
              && <ValidatingIcon className={classes.draft}/>}
              {(process.isProcessing() || process.isCompleted())
              && <ProcessingIcon className={classes.processing}/>}
              &nbsp;{t(toSentence(process.processType))} <small>({t(`Your process is ${process.state}`)})</small>
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Duration')}: {process.durationValue()} {t(process.durationLabel()).toLowerCase()}.
            </Typography>
            <Typography variant="subtitle2" className={classes.grey}>
              {t('Price')}: ${process.price.toFixed(2)}.
            </Typography>
            <small>&nbsp;</small>
            <ProcessAgreementData
              isRetailClient={isRetailClient}
              isValidation={false}
              code="agreement-data"
              disabled={processing || process.hasDocumentsValidated()}
              processing={processing}
              onSave={saveAgreementData}
              contractAgreementUrl={contractAgreementUrl}
              templateAgreementUrl={templateAgreementUrl}
              expanded
              onExpanded={(key) => (event, isExpanded) => {}}
              process={process}/>
          </>
        )}
      </div>
      {(agentPhone && user) && <List>
        <ListItem
          button
          key="agentPhone"
          classes={{ button: classes.support }}
          onClick={() => {
            const url = whatsappURL(agentPhone, t('Agent support text', user));
            window.open(url, '_blank');
          }}
        >
          <ListItemIcon>
            <SupportIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Agent phone')}/>
        </ListItem>
      </List>}
      {(supportPhone && user) && <List>
        <ListItem
          button
          key="supportPhone"
          classes={{ button: classes.support }}
          onClick={() => {
            const url = whatsappURL(supportPhone, t('Support text', user));
            window.open(url, '_blank');
          }}
        >
          <ListItemIcon>
            <SupportIcon className={classes.support}/>
          </ListItemIcon>
          <ListItemText primary={t('Support phone')}/>
        </ListItem>
      </List>}
    </Container>
  );
};

export default Contract;
